import {trackEvent} from './TrackingV2';
import {getBuilderIdentity} from './baselineBuilderSession';

/**
 * Baseline Builder (SoW-driven project creation) analytics. The object name is locked: renaming
 * an event loses its Amplitude history.
 *
 * No property ever carries document content, extracted values, exception text, filenames or
 * customer-defined names. Counts and fixed enumerations only.
 */
const BASELINE_BUILDER = 'Baseline Builder';

export const BASELINE_BUILDER_ENTRY_POINT = {GLOBAL_CREATE: 'Global Create', PROJECT_LIST: 'Project List'} as const;
export type BaselineBuilderEntryPoint = (typeof BASELINE_BUILDER_ENTRY_POINT)[keyof typeof BASELINE_BUILDER_ENTRY_POINT];

export const BUILDER_FILE_REJECT_REASON = {
	UNSUPPORTED_FORMAT: 'Unsupported Format',
	OVERSIZED: 'Oversized',
	READ_ERROR: 'Read Error',
} as const;
export type BuilderFileRejectReason = (typeof BUILDER_FILE_REJECT_REASON)[keyof typeof BUILDER_FILE_REJECT_REASON];

export const BUILDER_STAGE = {
	UPLOAD: 'Upload',
	EXTRACTING: 'Extracting',
	REVIEWING: 'Reviewing',
	COMMITTING: 'Committing',
} as const;
export type BuilderStage = (typeof BUILDER_STAGE)[keyof typeof BUILDER_STAGE];

export const BUILDER_SKIP_REASON = {
	FIELD_ALREADY_FILLED: 'Field Already Filled',
	UNANSWERED_AT_CONFIRM: 'Unanswered At Confirm',
	USER_SKIPPED: 'User Skipped',
} as const;
export type BuilderSkipReason = (typeof BUILDER_SKIP_REASON)[keyof typeof BUILDER_SKIP_REASON];

export const BUILDER_ANSWER_SOURCE = {OPTION: 'Option', FREE_TEXT: 'Free Text'} as const;
export type BuilderAnswerSource = (typeof BUILDER_ANSWER_SOURCE)[keyof typeof BUILDER_ANSWER_SOURCE];

export const BUILDER_EDIT_TYPE = {CHANGED: 'Changed', FILLED: 'Filled', CLEARED: 'Cleared'} as const;
export type BuilderEditType = (typeof BUILDER_EDIT_TYPE)[keyof typeof BUILDER_EDIT_TYPE];

// ── Mappings from the agent's stream values ─────────────────────────────────────
// Every stream field below is optional on the wire, so an absent or unrecognised value maps to a
// fixed fallback rather than being passed through.

const lookup = (table: Record<string, string>, raw: unknown, fallback: string): string =>
	typeof raw === 'string' && Object.prototype.hasOwnProperty.call(table, raw) ? table[raw] : fallback;

const EXTRACTION_OUTCOME: Record<string, string> = {
	full: 'Full',
	deliverables_only: 'Deliverables Only',
	roles_only: 'Roles Only',
	metadata_only: 'Metadata Only',
	empty: 'Empty',
};
export const toExtractionOutcome = (raw: unknown): string => lookup(EXTRACTION_OUTCOME, raw, 'Unknown');

const BLOCK_REASON: Record<string, string> = {
	NO_EXTRACTABLE_TEXT: 'No Extractable Text',
	SCANNED_DOCUMENT: 'Scanned Document',
	UNREADABLE_FILE: 'Unreadable File',
	RETAINER: 'Retainer',
	MSA: 'MSA',
	NOT_A_SOW: 'Not A SoW',
	NON_ENGLISH: 'Non English',
};
export const toBlockReason = (blockCode: unknown): string => lookup(BLOCK_REASON, blockCode, 'Unknown');

const QUESTION_TYPE: Record<string, string> = {
	entity: 'Entity',
	project_name: 'Project Name',
	role: 'Role',
	rate_card: 'Rate Card',
	value_conflict: 'Value Conflict',
	ambiguous: 'Ambiguous',
	date: 'Date',
	other: 'Other',
};
export const toQuestionType = (raw: unknown): string => lookup(QUESTION_TYPE, raw, 'Other');

const FAILURE_BUCKET: Record<string, string> = {
	PROJECT_CREATE_FAILED: 'Project Create Failed',
	INVOICE_CREATE_FAILED: 'Invoice Create Failed',
	CANCELLED_OR_TIMED_OUT: 'Cancelled Or Timed Out',
};
export const toFailureBucket = (failureCode: unknown): string => lookup(FAILURE_BUCKET, failureCode, 'Other');

// Skipped-role reasons, split into the role that could not be created and the role there was
// nothing to create from.
const ROLE_FAIL_REASONS = new Set(['no_phase', 'create_failed']);
const ROLE_SKIP_REASONS = new Set(['no_hours', 'unresolved_rate_card']);

/** Both counts, or nothing at all when the stream did not report skipped roles. */
export const toRoleSkipCounts = (
	skippedRoleReasons: string[] | undefined
): {roleFailCount: number; roleSkippedCount: number} | Record<string, never> => {
	if (!Array.isArray(skippedRoleReasons)) return {};
	return {
		roleFailCount: skippedRoleReasons.filter(reason => ROLE_FAIL_REASONS.has(reason)).length,
		roleSkippedCount: skippedRoleReasons.filter(reason => ROLE_SKIP_REASONS.has(reason)).length,
	};
};

const SUPPORTED_FILE_TYPES: ReadonlySet<string> = new Set(['pdf', 'docx', 'md']);

/** The lowercase extension when it is a supported document type, otherwise Other. Never any other part of the name. */
export const toFileType = (filename: string): string => {
	const dot = filename.lastIndexOf('.');
	const extension = dot < 0 ? '' : filename.slice(dot + 1).toLowerCase();
	return SUPPORTED_FILE_TYPES.has(extension) ? extension : 'Other';
};

const SYSTEM = {isSystemEvent: true} as const;

const builder = (name: string, props: Record<string, unknown> = {}): void => {
	trackEvent(BASELINE_BUILDER, name, {...props, ...getBuilderIdentity()});
};

// ── Events ──────────────────────────────────────────────────────────────────────

export const trackBaselineBuilderOpened = (entryPoint?: BaselineBuilderEntryPoint): void =>
	builder('Opened', entryPoint ? {entryPoint} : {});

export const trackBaselineBuilderFileRejected = (rejectReason: BuilderFileRejectReason): void =>
	builder('File Rejected', {rejectReason});

export const trackBaselineBuilderDocumentUploaded = (file: {name: string; size: number}): void =>
	builder('Document Uploaded', {fileType: toFileType(file.name), fileSizeKb: Math.round(file.size / 1024)});

export const trackBaselineBuilderExtractionCompleted = (p: {
	extractionDurationMs?: number;
	deliverableCount: number;
	roleCount: number;
	populatedFieldCount: number;
	emptyFieldCount: number;
	extractionOutcome: unknown;
}): void => {
	const {extractionDurationMs, extractionOutcome, ...counts} = p;
	builder('Extraction Completed', {
		...counts,
		...(extractionDurationMs != null ? {extractionDurationMs} : {}),
		extractionOutcome: toExtractionOutcome(extractionOutcome),
		...SYSTEM,
	});
};

export const trackBaselineBuilderExtractionBlocked = (blockReason: string): void =>
	builder('Extraction Blocked', {blockReason, ...SYSTEM});

export const trackBaselineBuilderExtractionFailed = (): void => builder('Extraction Failed', {...SYSTEM});

export const trackBaselineBuilderQuestionAsked = (rawQuestionType: unknown): void =>
	builder('Question Asked', {questionType: toQuestionType(rawQuestionType), ...SYSTEM});

export const trackBaselineBuilderQuestionAnswered = (rawQuestionType: unknown, answerSource?: BuilderAnswerSource): void =>
	builder('Question Answered', {questionType: toQuestionType(rawQuestionType), ...(answerSource ? {answerSource} : {})});

export const trackBaselineBuilderQuestionSkipped = (rawQuestionType: unknown, skipReason: BuilderSkipReason): void =>
	builder('Question Skipped', {questionType: toQuestionType(rawQuestionType), skipReason});

export const trackBaselineBuilderFieldEdited = (p: {
	fieldName: string;
	editType: BuilderEditType;
	hadValidationError: boolean;
}): void => builder('Field Edited', p);

export const trackBaselineBuilderDraftConfirmed = (p: {
	editedFieldCount: number;
	timeToConfirmSeconds?: number;
	questionCount: number;
	unansweredQuestionCount: number;
}): void => {
	const {timeToConfirmSeconds, ...rest} = p;
	builder('Draft Confirmed', {...rest, ...(timeToConfirmSeconds != null ? {timeToConfirmSeconds} : {})});
};

export const trackBaselineBuilderProjectCreated = (p: {
	deliverableCreatedCount?: number;
	deliverableSkippedCount?: number;
	roleCreatedCount?: number;
	skippedRoleReasons?: string[];
	commitDurationMs?: number;
}): void => {
	const {skippedRoleReasons, ...rest} = p;
	const defined = Object.fromEntries(Object.entries(rest).filter(([, value]) => value != null));
	builder('Project Created', {...defined, ...toRoleSkipCounts(skippedRoleReasons), ...SYSTEM});
};

export const trackBaselineBuilderCreationFailed = (failureCode?: unknown): void =>
	builder('Creation Failed', {failureBucket: toFailureBucket(failureCode), ...SYSTEM});

export const trackBaselineBuilderFileReplaced = (blockReason: string): void => builder('File Replaced', {blockReason});

export const trackBaselineBuilderSwitchedToManual = (blockReason: string): void => builder('Switched To Manual', {blockReason});

export const trackBaselineBuilderClosedWithoutSaving = (p: {stage: BuilderStage; editedFieldCount: number}): void =>
	builder('Closed Without Saving', p);
